import React, { useCallback, useId, useRef, useState } from 'react';

import { Box, ButtonBase, Typography } from '@mui/material';
import { ArrowIcon } from 'app/shared/componentsRestyled/coreDesignSystem/icons';
import PropTypes from 'prop-types';

import MilestoneRoadmapDocumentRow from './components/MilestoneRoadmapDocumentRow';
import {
  COLLAPSED_DOCUMENTS_COUNT,
  getShowAllLabel,
  getToggleLabel,
  SHOW_FEWER_LABEL,
} from './MilestoneRoadmapDocuments.constants';
import {
  getToggleIconStyles,
  listStyles,
  showAllStyles,
  toggleStyles,
  wrapperStyles,
} from './MilestoneRoadmapDocuments.styles';
import { MilestoneRoadmapDocumentsTestIds } from './MilestoneRoadmapDocuments.testIds';

const MilestoneRoadmapDocuments = ({ documents, isExpandedByDefault = false }) => {
  const listId = useId();
  const listRef = useRef(null);
  const toggleRef = useRef(null);

  const [isExpanded, setIsExpanded] = useState(isExpandedByDefault);
  const [isShowingAll, setIsShowingAll] = useState(false);

  const isOverflowing = documents.length > COLLAPSED_DOCUMENTS_COUNT;
  const visibleDocuments = isShowingAll ? documents : documents.slice(0, COLLAPSED_DOCUMENTS_COUNT);

  const onToggle = useCallback(() => {
    const nextIsExpanded = !isExpanded;

    setIsExpanded(nextIsExpanded);

    if (!nextIsExpanded) {
      setIsShowingAll(false);
    }

    // Move focus with the state change so keyboard users land on what just appeared.
    requestAnimationFrame(() => (nextIsExpanded ? listRef : toggleRef).current?.focus?.());
  }, [isExpanded]);

  const onToggleShowAll = useCallback(() => {
    setIsShowingAll(prevIsShowingAll => !prevIsShowingAll);
  }, []);

  return (
    <Box sx={wrapperStyles}>
      <ButtonBase
        aria-controls={listId}
        aria-expanded={isExpanded}
        data-testid={MilestoneRoadmapDocumentsTestIds.TOGGLE}
        ref={toggleRef}
        sx={toggleStyles}
        onClick={onToggle}
      >
        <Typography variant="regular12">{getToggleLabel(documents.length)}</Typography>
        <Box sx={getToggleIconStyles(isExpanded)}>
          <ArrowIcon size={12} />
        </Box>
      </ButtonBase>

      {isExpanded && (
        <>
          <Box id={listId} ref={listRef} sx={listStyles} tabIndex={-1}>
            {visibleDocuments.map((doc, index) => (
              <MilestoneRoadmapDocumentRow document={doc} isFirst={index === 0} key={doc.id} />
            ))}
          </Box>

          {isOverflowing && (
            <ButtonBase
              data-testid={MilestoneRoadmapDocumentsTestIds.SHOW_ALL}
              sx={showAllStyles}
              onClick={onToggleShowAll}
            >
              <Typography variant="medium12">
                {isShowingAll ? SHOW_FEWER_LABEL : getShowAllLabel(documents.length)}
              </Typography>
            </ButtonBase>
          )}
        </>
      )}
    </Box>
  );
};

MilestoneRoadmapDocuments.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      attachedDate: PropTypes.string,
      isSentToBorrower: PropTypes.bool,
    }),
  ).isRequired,
  isExpandedByDefault: PropTypes.bool,
};

export default MilestoneRoadmapDocuments;
